import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import { Badge, Button, Card, Col, Dropdown, Form, InputGroup, Nav, Row, Table } from 'react-bootstrap';
import axiosInstance from '../services/axiosInterceptor';
import Swal from 'sweetalert2';
import useStudyYearSelector from './useStudyYearSelector';
import { SelectedStudentCard, StudentNameCell, statusBadge } from './leaveOptions';

// ใช้เวลาท้องถิ่นเสมอ — toISOString() จะเลื่อนวันเมื่อเป็นโซนเวลาไทย
const toISO = (date) => (
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
);
const parseISO = (iso) => new Date(`${iso}T00:00:00`);
const todayISO = () => toISO(new Date());

const thaiDate = (value) => (value ? new Date(value).toLocaleDateString('th-TH') : '');

const alertError = (title, err) =>
  Swal.fire({ icon: 'error', title, text: err?.response?.data?.message || '' });

const TABS = [
  { key: 'individual', label: 'สถิติรายบุคคล', icon: 'ri-user-search-line' },
  { key: 'overview', label: 'ภาพรวมจำนวนนักเรียน', icon: 'ri-group-line' },
  { key: 'returns', label: 'นักเรียนที่กลับบ้าน', icon: 'ri-home-4-line' }
];

const OVERVIEW_MODES = [
  { key: 'day', label: 'รายวัน' },
  { key: 'week', label: 'รายสัปดาห์' },
  { key: 'month', label: 'รายเดือน' }
];

// ช่วงวันที่ของโหมดที่เลือก โดยอิงวันที่ตั้งต้น (สัปดาห์เริ่มวันจันทร์)
const rangeOf = (mode, anchor) => {
  const date = parseISO(anchor);
  if (mode === 'week') {
    const monday = new Date(date);
    monday.setDate(date.getDate() - ((date.getDay() + 6) % 7));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { start: toISO(monday), end: toISO(sunday) };
  }
  if (mode === 'month') {
    return {
      start: toISO(new Date(date.getFullYear(), date.getMonth(), 1)),
      end: toISO(new Date(date.getFullYear(), date.getMonth() + 1, 0))
    };
  }
  return { start: anchor, end: anchor };
};

// ปุ่มย้อนหลัง/ถัดไป — ขยับทีละวัน สัปดาห์ หรือเดือน ตามโหมด
const shiftAnchor = (mode, anchor, step) => {
  const date = parseISO(anchor);
  if (mode === 'week') date.setDate(date.getDate() + step * 7);
  else if (mode === 'month') date.setMonth(date.getMonth() + step);
  else date.setDate(date.getDate() + step);
  return toISO(date);
};

const rangeLabel = (mode, range) => {
  if (mode === 'day') return thaiDate(range.start);
  if (mode === 'month') {
    return parseISO(range.start).toLocaleDateString('th-TH', { month: 'long', year: 'numeric' });
  }
  return `${thaiDate(range.start)} - ${thaiDate(range.end)}`;
};

const monthRange = (month) => ({
  start: `${month}-01`,
  end: toISO(new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0))
});

const StatTile = ({ label, value, unit = 'คน', variant = 'primary', hint }) => (
  <Col xs={6} md={3}>
    <Card className="card-one h-100">
      <Card.Body className="p-2 px-3">
        <div className="fs-xs text-secondary">{label}</div>
        <div className={`fs-4 fw-bold text-${variant}`}>
          {value}<span className="fs-xs text-secondary fw-normal ms-1">{unit}</span>
        </div>
        {hint && <div className="fs-xs text-secondary">{hint}</div>}
      </Card.Body>
    </Card>
  </Col>
);

const genderBadge = (gen) => (
  <Badge bg={gen === 'm' ? 'primary' : 'danger'}>{gen === 'm' ? 'ชาย' : 'หญิง'}</Badge>
);

// วันที่+เวลาที่ยามกดที่ประตู — server ส่งมาเป็น 'YYYY-MM-DD HH:mm' อยู่แล้ว
const shortDateTime = (value) => {
  if (!value) return '';
  const [date, time] = String(value).split(' ');
  return `${thaiDate(date)}${time ? ` ${time} น.` : ''}`;
};

// สถานะการกลับเข้าโรงเรียน คิดจาก checked_in_at ที่ยามกด เทียบกับวันที่นัดกลับ (server เป็นคนตัดสิน)
const RETURN_STATES = {
  returned: { label: 'กลับเข้าแล้ว', variant: 'success', rowClass: 'table-success' },
  late: { label: 'กลับเข้าเกินกำหนด', variant: 'warning', rowClass: 'table-warning' },
  overdue: { label: 'เกินกำหนด ยังไม่กลับ', variant: 'danger', rowClass: 'table-danger' },
  away: { label: 'ยังอยู่บ้าน', variant: 'info', rowClass: 'table-info' },
  not_out: { label: 'ยามยังไม่กดออก', variant: 'warning', rowClass: 'table-warning' }
};

const RETURN_FILTERS = [
  { key: 'all', label: 'ทั้งหมด', variant: 'dark', match: () => true },
  { key: 'returned', label: 'กลับเข้าแล้ว', variant: 'success', match: state => state === 'returned' || state === 'late' },
  { key: 'away', label: 'ยังอยู่บ้าน', variant: 'info', match: state => state === 'away' },
  { key: 'overdue', label: 'เกินกำหนด', variant: 'danger', match: state => state === 'overdue' },
  { key: 'not_out', label: 'ยามยังไม่กดออก', variant: 'warning', match: state => state === 'not_out' }
];

// สรุปของแต่ละเพศ — นับจากรายการทั้งหมด ไม่ผูกกับปุ่มกรอง เพื่อให้ตัวเลขคงที่ขณะไล่ดูทีละสถานะ
const GENDER_SUMMARY_ITEMS = [
  { label: 'กลับเข้าแล้ว', variant: 'success', match: state => state === 'returned' || state === 'late' },
  { label: 'ยังอยู่บ้าน', variant: 'info', match: state => state === 'away' },
  { label: 'เกินกำหนด', variant: 'danger', match: state => state === 'overdue' },
  { label: 'ยามยังไม่กดออก', variant: 'warning', match: state => state === 'not_out' }
];

// ─── แท็บที่ 1: สถิติรายบุคคล ────────────────────────────────────────────────
const IndividualTab = ({ yearId }) => {
  const [keyword, setKeyword] = useState('');
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [mode, setMode] = useState('month');
  const [month, setMonth] = useState(() => todayISO().slice(0, 7));
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [data, setData] = useState(null);
  const [searched, setSearched] = useState(false);

  useEffect(() => { setSelectedStudent(null); setData(null); setStudents([]); setSearched(false); }, [yearId]);

  const filter = useMemo(() => {
    if (mode === 'month') return month ? monthRange(month) : { start: '', end: '' };
    return { start: rangeStart, end: rangeEnd };
  }, [mode, month, rangeStart, rangeEnd]);

  // ค้นหาใหม่ = เปลี่ยนนักเรียน จึงล้างคนที่เลือกไว้เพื่อให้รายชื่อกลับมาแสดง
  const searchStudents = (e) => {
    e?.preventDefault();
    if (!yearId) return;
    setSelectedStudent(null);
    setData(null);
    setSearched(true);
    axiosInstance.get(`/api/discipline/conduct/students?yearId=${yearId}&keyword=${encodeURIComponent(keyword.trim())}`)
      .then(res => setStudents(res.data || []))
      .catch(err => alertError('ไม่สามารถค้นหานักเรียนได้', err));
  };

  useEffect(() => {
    if (!selectedStudent || !yearId) return;
    const params = new URLSearchParams({ studentId: selectedStudent.st_id, yearId });
    if (filter.start) params.append('start', filter.start);
    if (filter.end) params.append('end', filter.end);
    axiosInstance.get(`/api/discipline/home-leave/stats/student?${params.toString()}`)
      .then(res => setData(res.data))
      .catch(err => alertError('ไม่สามารถโหลดสถิติได้', err));
  }, [selectedStudent, yearId, filter]);

  const summary = data?.summary;

  const periodHint = filter.start || filter.end
    ? `${filter.start ? thaiDate(filter.start) : 'ต้นปีการศึกษา'} - ${filter.end ? thaiDate(filter.end) : 'ปัจจุบัน'}`
    : 'ทั้งปีการศึกษา';

  return (
    <>
      {/* แถบเครื่องมือแถวเดียว — ค้นหานักเรียนและช่วงเวลาอยู่บรรทัดเดียวกัน */}
      <Card className="card-one mb-3">
        <Card.Body className="p-3 d-flex flex-wrap align-items-center gap-2">
          <Form onSubmit={searchStudents} className="flex-grow-1" style={{ minWidth: 220, maxWidth: 300 }}>
            <InputGroup size="sm">
              <Form.Control
                value={keyword}
                onChange={e => setKeyword(e.target.value)}
                placeholder="เลขประจำตัว หรือชื่อนักเรียน"
              />
              <Button type="submit"><i className="ri-search-line"></i></Button>
            </InputGroup>
          </Form>

          <div className="d-flex gap-2">
            <Button
              type="button"
              size="sm"
              variant={mode === 'month' ? 'primary' : 'outline-secondary'}
              onClick={() => setMode('month')}
            >
              รายเดือน
            </Button>
            <Button
              type="button"
              size="sm"
              variant={mode === 'range' ? 'primary' : 'outline-secondary'}
              onClick={() => setMode('range')}
            >
              ระหว่างวันที่
            </Button>
          </div>

          {mode === 'month' ? (
            <div className="d-flex align-items-center gap-2">
              <Form.Control
                size="sm"
                type="month"
                style={{ width: 165 }}
                value={month}
                onChange={e => setMonth(e.target.value)}
              />
              <Button
                type="button"
                size="sm"
                variant="link"
                className="p-0 text-decoration-none"
                onClick={() => setMonth('')}
              >
                ทั้งปี
              </Button>
            </div>
          ) : (
            <div className="d-flex align-items-center gap-2">
              <Form.Control
                size="sm"
                type="date"
                style={{ width: 155 }}
                value={rangeStart}
                onChange={e => setRangeStart(e.target.value)}
              />
              <span className="fs-xs text-secondary">ถึง</span>
              <Form.Control
                size="sm"
                type="date"
                style={{ width: 155 }}
                value={rangeEnd}
                min={rangeStart}
                onChange={e => setRangeEnd(e.target.value)}
              />
            </div>
          )}

          <div className="ms-auto fs-xs text-secondary">{periodHint}</div>
        </Card.Body>
      </Card>

      {!selectedStudent ? (
        <Card className="card-one">
          <Card.Body className="p-2" style={{ maxHeight: 300, overflowY: 'auto' }}>
            {students.length === 0 ? (
              <div className="text-center text-secondary py-4">
                {searched
                  ? 'ไม่พบนักเรียนที่ค้นหา'
                  : 'ค้นหาด้วยเลขประจำตัวหรือชื่อ แล้วเลือกนักเรียนเพื่อดูสถิติการกลับบ้าน'}
              </div>
            ) : (
              <Row className="g-2">
                {students.map(student => (
                  <Col md={6} xl={4} key={student.st_id}>
                    <div
                      role="button"
                      className="border rounded p-2 h-100"
                      onClick={() => setSelectedStudent(student)}
                    >
                      <StudentNameCell student={student} />
                    </div>
                  </Col>
                ))}
              </Row>
            )}
          </Card.Body>
        </Card>
      ) : (
        <>
          <SelectedStudentCard student={selectedStudent} />

          <Row className="g-2 mb-3">
            <StatTile label="กลับบ้าน" value={summary?.approved ?? 0} unit="ครั้ง" />
            <StatTile
              label="รวมวันที่ไม่อยู่โรงเรียน"
              value={summary?.total_days ?? 0}
              unit="วัน"
              variant="info"
              hint={summary?.no_return_date ? `อีก ${summary.no_return_date} ครั้งยังไม่กำหนดวันกลับ` : ''}
            />
            <StatTile label="ไม่อนุญาต" value={summary?.rejected ?? 0} unit="ครั้ง" variant="danger" />
            <StatTile label="ยกเลิก" value={summary?.cancelled ?? 0} unit="ครั้ง" variant="secondary" />
          </Row>

          <Card className="card-one">
            <Card.Header>
              <Card.Title as="h6" className="mb-0">
                รายการกลับบ้าน
                <span className="fs-xs text-secondary fw-normal ms-2">{periodHint}</span>
              </Card.Title>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="table-secondary">
                    <tr>
                      <th>วันที่กลับบ้าน</th>
                      <th>วันที่มาโรงเรียน</th>
                      <th className="text-center">รวม</th>
                      <th>เหตุผล</th>
                      <th>ผู้ปกครอง</th>
                      <th className="text-center">สถานะ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.rows || []).map(row => (
                      <tr key={row.leave_id}>
                        <td>{thaiDate(row.leave_date)}</td>
                        <td>{row.return_date ? thaiDate(row.return_date) : <span className="text-secondary">ยังไม่กำหนด</span>}</td>
                        <td className="text-center">
                          {row.return_date ? `${Number(row.leave_days || 0) + 1} วัน` : '-'}
                        </td>
                        <td>{row.reason}</td>
                        <td>{row.guardian_name || '-'}</td>
                        <td className="text-center">{statusBadge(row.status)}</td>
                      </tr>
                    ))}
                    {(data?.rows || []).length === 0 && (
                      <tr><td colSpan={6} className="text-center text-secondary py-4">ไม่มีรายการในช่วงที่เลือก</td></tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </>
      )}
    </>
  );
};

// ─── แท็บที่ 2: ภาพรวมจำนวนนักเรียนที่กลับบ้าน ──────────────────────────────
const OverviewTab = ({ yearId }) => {
  const [mode, setMode] = useState('day');
  const [anchor, setAnchor] = useState(todayISO);
  const [classMode, setClassMode] = useState('general');
  const [data, setData] = useState(null);

  const range = useMemo(() => rangeOf(mode, anchor), [mode, anchor]);

  const fetchSummary = useCallback(() => {
    if (!yearId) return;
    const params = new URLSearchParams({ yearId, start: range.start, end: range.end, asOf: anchor });
    axiosInstance.get(`/api/discipline/home-leave/stats/summary?${params.toString()}`)
      .then(res => setData(res.data))
      .catch(err => alertError('ไม่สามารถโหลดภาพรวมได้', err));
  }, [yearId, range, anchor]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const departed = data?.departed;
  const away = data?.away;

  // ตารางแยกชั้น — สลับระหว่างชั้นสามัญ (tb_class) กับชั้นศาสนา (is_class)
  const classRows = (classMode === 'islamic' ? data?.by_islamic_class : data?.by_class) || [];
  const classKey = (row) => (classMode === 'islamic' ? row.ic_id : row.c_id);
  const classLabel = (row) => {
    if (classMode === 'islamic') return row.ic_class || 'ไม่ระบุชั้นศาสนา';
    return row.c_id ? `ม.${row.c_id}` : 'ไม่ระบุชั้น';
  };

  return (
    <>
      <Card className="card-one mb-3">
        <Card.Body className="d-flex flex-wrap align-items-center gap-3">
          <div className="d-flex flex-wrap gap-2">
            {OVERVIEW_MODES.map(item => (
              <Button
                key={item.key}
                type="button"
                size="sm"
                variant={mode === item.key ? 'primary' : 'outline-secondary'}
                onClick={() => setMode(item.key)}
              >
                {item.label}
              </Button>
            ))}
          </div>
          <InputGroup style={{ width: 260 }}>
            <Button variant="outline-secondary" onClick={() => setAnchor(shiftAnchor(mode, anchor, -1))} title="ย้อนหลัง">
              <i className="ri-arrow-left-s-line"></i>
            </Button>
            <Form.Control type="date" value={anchor} onChange={e => e.target.value && setAnchor(e.target.value)} />
            <Button variant="outline-secondary" onClick={() => setAnchor(shiftAnchor(mode, anchor, 1))} title="ถัดไป">
              <i className="ri-arrow-right-s-line"></i>
            </Button>
          </InputGroup>
          <Button variant="outline-primary" size="sm" onClick={() => setAnchor(todayISO())}>วันนี้</Button>
          <div className="ms-auto fs-sm text-secondary">{rangeLabel(mode, range)}</div>
        </Card.Body>
      </Card>

      <Row className="g-2 mb-3">
        <StatTile label={`กลับบ้าน (${rangeLabel(mode, range)})`} value={departed?.total ?? 0} />
        <StatTile label="ชาย" value={departed?.male ?? 0} variant="info" />
        <StatTile label="หญิง" value={departed?.female ?? 0} variant="danger" />
        <StatTile
          label="ยังไม่กลับเข้าโรงเรียน"
          value={away?.total ?? 0}
          variant="warning"
          hint={`ณ ${thaiDate(anchor)} · ชาย ${away?.male ?? 0} / หญิง ${away?.female ?? 0}`}
        />
      </Row>

      <Row className="g-3">
        <Col xl={4}>
          <Card className="card-one">
            <Card.Header>
              <Card.Title as="h6" className="mb-0">แยกตามชั้นเรียน</Card.Title>
              <div className="ms-auto d-flex gap-3">
                <Form.Check
                  type="radio"
                  id="home-leave-class-general"
                  name="home-leave-class-mode"
                  label="สามัญ"
                  checked={classMode === 'general'}
                  onChange={() => setClassMode('general')}
                />
                <Form.Check
                  type="radio"
                  id="home-leave-class-islamic"
                  name="home-leave-class-mode"
                  label="ศาสนา"
                  checked={classMode === 'islamic'}
                  onChange={() => setClassMode('islamic')}
                />
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="table-secondary">
                    <tr>
                      <th>ชั้น</th>
                      <th className="text-center">ชาย</th>
                      <th className="text-center">หญิง</th>
                      <th className="text-center">รวม</th>
                    </tr>
                  </thead>
                  <tbody>
                    {classRows.map(row => (
                      <tr key={classKey(row) || 'unknown'}>
                        <td>{classLabel(row)}</td>
                        <td className="text-center">{row.male}</td>
                        <td className="text-center">{row.female}</td>
                        <td className="text-center fw-semibold">{row.total}</td>
                      </tr>
                    ))}
                    {classRows.length === 0 && (
                      <tr><td colSpan={4} className="text-center text-secondary py-4">ไม่มีข้อมูล</td></tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xl={8}>
          <Card className="card-one">
            <Card.Header>
              <Card.Title as="h6" className="mb-0">
                รายชื่อนักเรียนที่กลับบ้าน
                <span className="fs-xs text-secondary fw-normal ms-2">{(data?.rows || []).length} รายการ</span>
              </Card.Title>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="table-secondary">
                    <tr>
                      <th>นักเรียน</th>
                      <th className="text-center">เพศ</th>
                      <th>วันที่กลับบ้าน</th>
                      <th>วันที่มาโรงเรียน</th>
                      <th>เหตุผล</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.rows || []).map(row => (
                      <tr key={row.leave_id}>
                        <td><StudentNameCell student={row} /></td>
                        <td className="text-center">{genderBadge(row.st_gen)}</td>
                        <td>{thaiDate(row.leave_date)}</td>
                        <td>
                          {row.return_date ? thaiDate(row.return_date) : <span className="text-secondary">ยังไม่กำหนด</span>}
                        </td>
                        <td>{row.reason}</td>
                      </tr>
                    ))}
                    {(data?.rows || []).length === 0 && (
                      <tr><td colSpan={5} className="text-center text-secondary py-4">ไม่มีนักเรียนกลับบ้านในช่วงนี้</td></tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

// ─── แท็บที่ 3: นักเรียนที่กลับบ้าน แยกชาย/หญิง ──────────────────────────────
// สีของแถวมาจากการยืนยันของยามที่ประตูเท่านั้น — เขียว = กลับเข้าตรงหรือก่อนกำหนด
// แดง = เลยวันที่นัดกลับแล้วยามยังไม่กดกลับเข้า
const GenderReturnTable = ({ title, icon, variant, rows }) => {
  const overdue = rows.filter(row => row.return_state === 'overdue').length;
  return (
    <Card className="card-one h-100">
      <Card.Header className="d-flex flex-wrap align-items-center gap-2">
        <Card.Title as="h6" className="mb-0">
          <i className={`${icon} me-1 text-${variant}`}></i>{title}
        </Card.Title>
        <div className="ms-auto d-flex gap-2">
          <Badge bg="secondary">{rows.length} รายการ</Badge>
          {overdue > 0 && <Badge bg="danger">เกินกำหนด {overdue}</Badge>}
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        <div className="table-responsive">
          <Table hover className="mb-0">
            <thead className="table-secondary">
              <tr>
                <th>นักเรียน</th>
                <th>ออก - กำหนดกลับ</th>
                <th>ผู้อนุญาต</th>
                <th>ยามยืนยัน</th>
                <th className="text-center">สถานะ</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const state = RETURN_STATES[row.return_state] || RETURN_STATES.away;
                return (
                  <tr key={row.leave_id} className={state.rowClass}>
                    <td><StudentNameCell student={row} /></td>
                    <td>
                      <div>{thaiDate(row.leave_date)}</div>
                      <div className="fs-xs text-secondary">
                        {row.return_date ? `กำหนดกลับ ${thaiDate(row.return_date)}` : 'ยังไม่กำหนดวันกลับ'}
                      </div>
                    </td>
                    <td className="fs-xs">
                      {/* ใบที่บันทึกพร้อมอนุมัติในตัว ผู้บันทึกคือผู้อนุญาต — ถ้าไม่มีค่อยแสดงผู้บันทึก */}
                      <div>{row.approved_by_name || row.created_by_name || <span className="text-secondary">ไม่ระบุ</span>}</div>
                      {row.approved_at && (
                        <div className="text-secondary">{shortDateTime(row.approved_at)}</div>
                      )}
                    </td>
                    <td className="fs-xs">
                      <div>
                        {row.checked_out_at
                          ? <><i className="ri-logout-box-r-line me-1"></i>ออก {shortDateTime(row.checked_out_at)}</>
                          : <span className="text-secondary">ยามยังไม่กดออก</span>}
                      </div>
                      {row.checked_out_by_name && (
                        <div className="text-secondary ms-3">ยาม {row.checked_out_by_name}</div>
                      )}
                      <div>
                        {row.checked_in_at
                          ? <><i className="ri-login-box-line me-1"></i>กลับเข้า {shortDateTime(row.checked_in_at)}</>
                          : <span className="text-secondary">ยังไม่กลับเข้า</span>}
                      </div>
                      {row.checked_in_by_name && (
                        <div className="text-secondary ms-3">ยาม {row.checked_in_by_name}</div>
                      )}
                    </td>
                    <td className="text-center align-middle">
                      <Badge bg={state.variant}>{state.label}</Badge>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={5} className="text-center text-secondary py-4">ไม่มีรายการ</td></tr>
              )}
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );
};

// การ์ดสรุปย่อยของแต่ละเพศ — หอชายกับหอหญิงคนละทีมกัน จึงต้องเห็นตัวเลขของตัวเองแยกกัน
const GenderSummaryCard = ({ title, icon, variant, rows }) => (
  <Card className="card-one h-100">
    <Card.Body className="p-3">
      <div className="d-flex align-items-center gap-2 mb-2">
        <i className={`${icon} text-${variant}`}></i>
        <span className="fw-semibold">{title}</span>
        <Badge bg={variant} className="ms-auto">{rows.length} รายการ</Badge>
      </div>
      <Row className="g-2">
        {GENDER_SUMMARY_ITEMS.map(item => (
          <Col xs={3} key={item.label}>
            <div className="fs-xs text-secondary">{item.label}</div>
            <div className={`fs-5 fw-bold text-${item.variant}`}>
              {rows.filter(row => item.match(row.return_state)).length}
            </div>
          </Col>
        ))}
      </Row>
    </Card.Body>
  </Card>
);

const ReturnsTab = ({ yearId }) => {
  const [mode, setMode] = useState('month');
  const [anchor, setAnchor] = useState(todayISO);
  const [stateFilter, setStateFilter] = useState('all');
  const [rows, setRows] = useState([]);

  const range = useMemo(() => rangeOf(mode, anchor), [mode, anchor]);
  // ดูช่วงที่ผ่านไปแล้ว ให้ตัดสิน "เกินกำหนด" ณ วันสุดท้ายของช่วงนั้น ไม่ใช่วันนี้
  const asOf = useMemo(() => {
    const today = todayISO();
    return range.end < today ? range.end : today;
  }, [range]);

  useEffect(() => {
    if (!yearId) return;
    const params = new URLSearchParams({ yearId, start: range.start, end: range.end, asOf });
    axiosInstance.get(`/api/discipline/home-leave/stats/returns?${params.toString()}`)
      .then(res => setRows(res.data || []))
      .catch(err => alertError('ไม่สามารถโหลดรายชื่อนักเรียนที่กลับบ้านได้', err));
  }, [yearId, range, asOf]);

  const filtered = useMemo(() => {
    const filter = RETURN_FILTERS.find(item => item.key === stateFilter) || RETURN_FILTERS[0];
    return rows.filter(row => filter.match(row.return_state));
  }, [rows, stateFilter]);

  const maleRows = filtered.filter(row => row.st_gen === 'm');
  const femaleRows = filtered.filter(row => row.st_gen === 'f');
  const unknownCount = filtered.length - maleRows.length - femaleRows.length;

  const allMaleRows = rows.filter(row => row.st_gen === 'm');
  const allFemaleRows = rows.filter(row => row.st_gen === 'f');

  const countOf = (match) => rows.filter(row => match(row.return_state)).length;
  const returnedCount = countOf(state => state === 'returned');
  const lateCount = countOf(state => state === 'late');
  const overdueCount = countOf(state => state === 'overdue');
  const awayCount = countOf(state => state === 'away');
  const notOutCount = countOf(state => state === 'not_out');

  return (
    <>
      <Card className="card-one mb-3">
        <Card.Body className="d-flex flex-wrap align-items-center gap-3">
          <div className="d-flex flex-wrap gap-2">
            {OVERVIEW_MODES.map(item => (
              <Button
                key={item.key}
                type="button"
                size="sm"
                variant={mode === item.key ? 'primary' : 'outline-secondary'}
                onClick={() => setMode(item.key)}
              >
                {item.label}
              </Button>
            ))}
          </div>
          <InputGroup style={{ width: 260 }}>
            <Button variant="outline-secondary" onClick={() => setAnchor(shiftAnchor(mode, anchor, -1))} title="ย้อนหลัง">
              <i className="ri-arrow-left-s-line"></i>
            </Button>
            <Form.Control type="date" value={anchor} onChange={e => e.target.value && setAnchor(e.target.value)} />
            <Button variant="outline-secondary" onClick={() => setAnchor(shiftAnchor(mode, anchor, 1))} title="ถัดไป">
              <i className="ri-arrow-right-s-line"></i>
            </Button>
          </InputGroup>
          <Button variant="outline-primary" size="sm" onClick={() => setAnchor(todayISO())}>วันนี้</Button>
          <div className="d-flex flex-wrap gap-1">
            {RETURN_FILTERS.map(item => (
              <Button
                key={item.key}
                type="button"
                size="sm"
                variant={stateFilter === item.key ? item.variant : `outline-${item.variant}`}
                onClick={() => setStateFilter(item.key)}
              >
                {item.label}
              </Button>
            ))}
          </div>
          <div className="ms-auto fs-sm text-secondary">{rangeLabel(mode, range)}</div>
        </Card.Body>
      </Card>

      <Row className="g-2 mb-3">
        <StatTile
          label="กลับเข้าตรงเวลา"
          value={returnedCount}
          variant="success"
          hint={lateCount > 0 ? `กลับเข้าเกินกำหนดอีก ${lateCount} คน` : ''}
        />
        <StatTile
          label="ยังอยู่บ้าน (ยังไม่ถึงกำหนด)"
          value={awayCount}
          variant="info"
          hint={notOutCount > 0 ? `ยามยังไม่กดออกอีก ${notOutCount} คน` : ''}
        />
        <StatTile label="เกินกำหนด ยังไม่กลับ" value={overdueCount} variant="danger" hint={`ณ ${thaiDate(asOf)}`} />
        <StatTile label="รวมทั้งหมด" value={rows.length} unit="รายการ" />
      </Row>

      <Row className="g-3 mb-3">
        <Col md={6}>
          <GenderSummaryCard title="นักเรียนชาย" icon="ri-men-line" variant="primary" rows={allMaleRows} />
        </Col>
        <Col md={6}>
          <GenderSummaryCard title="นักเรียนหญิง" icon="ri-women-line" variant="danger" rows={allFemaleRows} />
        </Col>
      </Row>

      {unknownCount > 0 && (
        <div className="fs-xs text-secondary mb-2">
          * อีก {unknownCount} รายการไม่ได้ระบุเพศในทะเบียนนักเรียน จึงไม่แสดงในตารางด้านล่าง
        </div>
      )}

      <Row className="g-3">
        <Col xl={6}>
          <GenderReturnTable title="นักเรียนชาย" icon="ri-men-line" variant="primary" rows={maleRows} />
        </Col>
        <Col xl={6}>
          <GenderReturnTable title="นักเรียนหญิง" icon="ri-women-line" variant="danger" rows={femaleRows} />
        </Col>
      </Row>
    </>
  );
};

export default function DisciplineHomeLeaveStats() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];
  const { yearList, yearId, yearLabel, handleSelectYear } = useStudyYearSelector();
  const [activeTab, setActiveTab] = useState('individual');

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item">ฝ่ายปกครอง</li>
              <li className="breadcrumb-item active">สถิติการกลับบ้าน</li>
            </ol>
            <h4 className="main-title mb-0">สถิติการกลับบ้าน</h4>
          </div>
          <Dropdown className="mt-3 mt-md-0">
            <Dropdown.Toggle variant="primary" id="home-leave-stats-year">
              {yearLabel || 'เลือกปีการศึกษา'}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {yearList.map(year => (
                <Dropdown.Item key={year.year_id} onClick={() => handleSelectYear(year.year_id)}>
                  {`ปีการศึกษา ${year.year_year}`}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        <Nav variant="tabs" className="mb-3" activeKey={activeTab} onSelect={key => setActiveTab(key)}>
          {TABS.map(tab => (
            <Nav.Item key={tab.key}>
              <Nav.Link eventKey={tab.key}>
                <i className={`${tab.icon} me-1`}></i>{tab.label}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>

        {activeTab === 'individual' && <IndividualTab yearId={yearId} />}
        {activeTab === 'overview' && <OverviewTab yearId={yearId} />}
        {activeTab === 'returns' && <ReturnsTab yearId={yearId} />}

        <Footer />
      </div>
    </>
  );
}
